import { createAsyncThunk, createDraftSafeSelector, createSlice } from "@reduxjs/toolkit";
import Axios from "axios";
import BigNumber from "bignumber.js";
import { getCurrentContractAddresses } from "src/configs/address";
import { BASE18, RENT_CONTRACT_TIME_BASE } from "src/configs/constance";
import kawaiiArtData from "src/configs/kwaii";
import { resetAccountState } from "src/redux/account-slice";
import { TICKET_IDS, TICKET_TO_ID } from "src/redux/ticket-slice";
import { getVeTravaRank, isNumeric } from "src/utils";
import { CollectionName } from "src/utils/knight";
import KawaiiCoreContract from "src/contract-logic/logic/KawaiiCoreContract";
import NFTAuctionContract from "src/contract-logic/logic/NFTAuctionContract";
import NFTBuyContract from "src/contract-logic/logic/NFTBuyContract";
import NFTCollectionContract from "src/contract-logic/logic/NFTCollectionContract";
import NFTCoreContract from "src/contract-logic/logic/NFTCoreContract";
import NFTRentContract from "src/contract-logic/logic/NFTRentContract";
import NFTSellContract from "src/contract-logic/logic/NFTSellContract";
import NFTTicketContract from "src/contract-logic/logic/NFTTicketContract";
import NFTTicketMarketplaceContract from "src/contract-logic/logic/NFTTicketMarketplaceContract";
import TokenFaucetContract from "src/contract-logic/logic/TokenFaucetContract";
import VeTravaContract from "src/contract-logic/logic/VeTravaContract";
import VeTravaMarketPlaceContract from "src/contract-logic/logic/VeTravaMarketPlaceContract";
import multiCall from "src/utils/multicall";

const client = Axios.create({ baseURL: "https://backend.trava.finance/nft/api" });

function groupHistoriesByDay(data) {
  let res = {};
  data.forEach((item) => {
    const localeDateStr = new Date(item.startTime * 1000).toLocaleDateString();
    const id = new Date(localeDateStr).getTime();
    res[id] = [...(res[id] || []), { ...item, startTime: item.startTime * 1000 }];
  });
  return res;
}

export const fetchLeaseOrderData = createAsyncThunk("profileSlice/fetchLeaseOrderData", async (_, thunkAPI) => {
  const accountAddress = thunkAPI.getState().accountSlice.address;
  const res = await client.get(`/v1/getRentingNFT?address=${accountAddress}`).then((res) => res.data);
  return res.data;
});

export const fetchDepositHistory = createAsyncThunk(
  "profileSlice/fetchDepositHistory",
  async ({ accountAddress, page = 0, size = 5 }, thunkAPI) => {
    let currentLength = selectProfileSlice(thunkAPI.getState()).history.deposit?.list?.length || 0;
    let total = selectDepositHistoryMetadata(thunkAPI.getState())?.total || 0;
    let list = [];
    if ((page + 1) * size > currentLength && (total === 0 || currentLength < total)) {
      const res = await client
        .get(`/v1/getRentCreated?page=${page}&size=${size}&address=${accountAddress}`)
        .then((res) => res.data);
      if (!res.success) throw new Error(res.message);
      list = res.data.list;
      total = res.data.total;
    }
    return { list, metadata: { total, page, size } };
  }
);

export const fetchRentHistory = createAsyncThunk(
  "profileSlice/fetchRentHistory",
  async ({ accountAddress, page = 0, size = 5 }, thunkAPI) => {
    let currentLength = selectProfileSlice(thunkAPI.getState()).history.rent?.list?.length || 0;
    let total = selectRentHistoryMetadata(thunkAPI.getState())?.total || 0;
    let list = [];
    if ((page + 1) * size > currentLength && (total === 0 || currentLength < total)) {
      const res = await client
        .get(`/v1/getRentActivated?page=${page}&size=${size}&address=${accountAddress}`)
        .then((res) => res.data);
      if (!res.success) throw new Error(res.message);
      list = res.data.list;
      total = res.data.total;
    }
    return { list, metadata: { total, page, size } };
  }
);

export const fetchWithdrawHistory = createAsyncThunk(
  "profileSlice/fetchWithdrawHistory",
  async ({ accountAddress, page = 0, size = 5 }, thunkAPI) => {
    let currentLength = selectProfileSlice(thunkAPI.getState()).history.withdraw?.list?.length || 0;
    let total = selectRentHistoryMetadata(thunkAPI.getState())?.total || 0;
    let list = [];
    if ((page + 1) * size > currentLength && (total === 0 || currentLength < total)) {
      const res = await client
        .get(`/v1/getNFTWithdrawn?page=${page}&size=${size}&address=${accountAddress}`)
        .then((res) => res.data);
      if (!res.success) throw new Error(res.message);
      list = res.data.list;
      total = res.data.total;
    }
    return { list, metadata: { total, page, size } };
  }
);

export const fetchKawaiiOwnedNFTs = createAsyncThunk("profileSlice/fetchKawaiiOwnedNFTs", async (_, thunkAPI) => {
  const accountAddress = thunkAPI.getState().accountSlice.address;
  const artData = Object.entries(kawaiiArtData).map((item) => ({ ...item[1], id: item[0] }));
  let owners = [];
  let tokenIds = [];
  artData.forEach((item) => {
    owners.push(accountAddress);
    tokenIds.push(item.id);
  });
  const quantities = await KawaiiCoreContract.getBatchQuantity(owners, tokenIds);
  return artData
    .map((item, idx) => ({ ...item, id: parseInt(item.id), quantity: parseInt(quantities[idx]) }))
    .filter((item) => item.quantity > 0);
});

export const fetchKawaiiLeasingNFTs = createAsyncThunk(
  "profileSlice/fetchKawaiiLeasingNFTs",
  async ({ ownerAddress }) => {
    const tokenListSize = await NFTRentContract.getTokenListSizeOfOwner(ownerAddress);
    const [tokenIds] = await Promise.all([
      multiCall(
        NFTRentContract.ABI,
        new Array(parseInt(tokenListSize)).fill(1).map((_, idx) => ({
          address: NFTRentContract.ADDRESS,
          name: "tokenIdAtIndex",
          params: [ownerAddress, idx],
        }))
      ),
    ]);
    const tokenIdsFlattened = tokenIds.flat();
    const [nftRentListings, availableStocks] = await Promise.all([
      multiCall(
        NFTRentContract.ABI,
        tokenIdsFlattened.map((tokenId) => ({
          address: NFTRentContract.ADDRESS,
          name: "checkRentListing",
          params: [tokenId.toNumber(), ownerAddress],
        }))
      ),
      multiCall(
        NFTRentContract.ABI,
        tokenIdsFlattened.map((tokenId) => ({
          address: NFTRentContract.ADDRESS,
          name: "getAvailableStock",
          params: [ownerAddress, tokenId.toNumber()],
        }))
      ),
      [],
    ]);
    const quantities = await Promise.all(
      nftRentListings
        .flat()
        .map(
          async (item) =>
            parseInt(item.unallocatedTokens) +
            (await NFTRentContract.getTotalRentingNFT(item.orderHistory, item.allocatedTokens))
        )
    );
    return nftRentListings.flat().map((item, idx) => ({
      isBnB: item.isBnB,
      isAvailableForRent: item.isAvailableForRent,
      minTime: parseInt(parseInt(item.minTime) / RENT_CONTRACT_TIME_BASE),
      maxTime: parseInt(parseInt(item.maxTime) / RENT_CONTRACT_TIME_BASE),
      currentOwner: ownerAddress,
      price: parseFloat(item.price) / BASE18,
      id: parseInt(tokenIdsFlattened[idx]),
      ...kawaiiArtData[parseInt(tokenIdsFlattened[idx])],
      available: parseInt(availableStocks[idx][0]),
      quantity: quantities[idx],
      offsetAmount: 0,
    }));
  }
);

export const fetchVeTravaOwned = createAsyncThunk("profileSlice/fetchVeTravaOwned", async (_, thunkAPI) => {
  const accountAddress = thunkAPI.getState().accountSlice.address;
  const tokenLength = await VeTravaContract.getBalanceOf(accountAddress);

  const [tokenIds] = await Promise.all([
    multiCall(
      VeTravaContract.ABI,
      new Array(tokenLength).fill(1).map((_, idx) => ({
        address: VeTravaContract.ADDRESS,
        name: "tokenOfOwnerByIndex",
        params: [accountAddress, idx],
      }))
    ),
  ]);

  const tokenIdsFlattened = tokenIds.flat();

  const [tokensMetadata, tokenVotingPower] = await Promise.all([
    multiCall(
      VeTravaContract.ABI,
      tokenIdsFlattened.map((tokenId) => ({
        address: VeTravaContract.ADDRESS,
        name: "locked",
        params: [tokenId],
      }))
    ),
    multiCall(
      VeTravaContract.ABI,
      tokenIdsFlattened.map((tokenId) => ({
        address: VeTravaContract.ADDRESS,
        name: "balanceOfNFT",
        params: [tokenId],
      }))
    ),
  ]);

  const { TRAVA_VE_TOKEN_ADDRESS, RTRAVA_VE_TOKEN_ADDRESS } = getCurrentContractAddresses();
  const res = tokensMetadata.map((item, idx) => {
    const id = parseInt(tokenIdsFlattened[idx]);
    const amount = BigNumber(item.amount._hex).dividedBy(BASE18).toNumber();
    const rwAmount = BigNumber(item.rewardAmount._hex).dividedBy(BASE18).toNumber();
    const end = new Date(parseInt(item.end * 1000));
    const lockEnd = end.getDate() + "/" + (end.getMonth() + 1) + "/" + end.getFullYear();
    const token =
      item.token === TRAVA_VE_TOKEN_ADDRESS ? "TRAVA" : item.token === RTRAVA_VE_TOKEN_ADDRESS ? "rTRAVA" : "BNB/TRAVA";
    const baseAmount = BigNumber(item.baseAmount._hex).dividedBy(BASE18).toNumber();
    const votingPower = BigNumber(parseInt(tokenVotingPower[idx])).dividedBy(BASE18).toNumber();
    return { amount, rwAmount, lockEnd, token, baseAmount, votingPower, id };
  });
  return res;
});

export const fetchKawaiiRentingNFTs = createAsyncThunk(
  "profileSlice/fetchKawaiiRentingNFTs",
  async ({ ownerAddress }) => {
    const renter = ownerAddress;
    const renterInfo = await NFTRentContract.getRenterInfoPublic(renter);
    const tokenOwnersOfRenterHistory = renterInfo.tokenOwnersOfRenterHistory;
    const tokenIdsOfRenterHistory = renterInfo.tokenIdsOfRenterHistory;
    const orderIdsOfRenterHistory = renterInfo.orderIdsOfRenterHistory;
    const historyLength = tokenOwnersOfRenterHistory.length;

    const [isOrderExpiredArray, isValidOrderArray] = await Promise.all([
      multiCall(
        NFTRentContract.ABI,
        new Array(historyLength).fill(1).map((_, idx) => ({
          address: NFTRentContract.ADDRESS,
          name: "isOrderExpired",
          params: [orderIdsOfRenterHistory[idx]],
        }))
      ),
      multiCall(
        NFTRentContract.ABI,
        new Array(historyLength).fill(1).map((_, idx) => ({
          address: NFTRentContract.ADDRESS,
          name: "verifyRent",
          params: [tokenIdsOfRenterHistory[idx], tokenOwnersOfRenterHistory[idx], renter],
        }))
      ),
    ]);
    const isOrderExpiredArrayFlattened = isOrderExpiredArray.flat();
    const isValidOrderArrayFlattened = isValidOrderArray.flat();
    let rentActiveParams = [];
    isOrderExpiredArrayFlattened.forEach((isOrderExpired, idx) => {
      const isValidOrder = isValidOrderArrayFlattened[idx];
      const _tokenId = tokenIdsOfRenterHistory[idx];
      const _nftOwner = tokenOwnersOfRenterHistory[idx];
      const _nftRenter = renter;
      if (!isOrderExpired && isValidOrder) {
        rentActiveParams.push([_tokenId, _nftOwner, _nftRenter]);
      }
    });

    const [rentingList] = await Promise.all([
      multiCall(
        NFTRentContract.ABI,
        rentActiveParams.map((item) => ({
          address: NFTRentContract.ADDRESS,
          name: "checkRentActive",
          params: item,
        }))
      ),
    ]);

    return rentingList.flat().map((rentingNFT, idx) => ({
      ...kawaiiArtData[rentActiveParams[idx][0]],
      id: parseInt(rentActiveParams[idx][0]),
      quantity: parseInt(rentingNFT.amount),
      duration: parseInt(rentingNFT.duration) / RENT_CONTRACT_TIME_BASE,
      orderId: parseInt(rentingNFT.orderId),
      startTime: parseInt(rentingNFT.startTime) * 1000,
    }));
  }
);

export const fetchTravaSellingNFTs = createAsyncThunk(
  "profileSlice/fetchTravaSellingNFTs",
  async ({ ownerAddress }) => {
    const tokenLength = await NFTSellContract.getTokenOfOwnerBalance(ownerAddress);
    const [tokenIds] = await Promise.all([
      multiCall(
        NFTSellContract.ABI,
        new Array(tokenLength).fill(1).map((_, idx) => ({
          address: NFTSellContract.ADDRESS,
          name: "getTokenOfOwnerAtIndex",
          params: [ownerAddress, idx],
        }))
      ),
    ]);
    const tokenIdsFlattened = tokenIds.flat();
    const [tokensMetadata, ordersMetadata] = await Promise.all([
      multiCall(
        NFTCoreContract.ABI,
        tokenIdsFlattened.map((tokenId) => ({
          address: NFTCoreContract.ADDRESS,
          name: "getTokenMetadata",
          params: [tokenId],
        }))
      ),
      multiCall(
        NFTSellContract.ABI,
        tokenIdsFlattened.map((tokenId) => ({
          address: NFTSellContract.ADDRESS,
          name: "getTokenOrder",
          params: [tokenId],
        }))
      ),
    ]);
    const tokensMetadataFlattened = tokensMetadata.flat();
    const ordersMetadataFlattened = ordersMetadata.flat();

    let v1 = [];
    let v2 = [];
    let counter = 0;
    for (const tokenData of tokensMetadataFlattened) {
      const collectionId = parseInt(tokenData.collectionId);
      const collectionName = CollectionName[collectionId - 1];
      if (collectionName) {
        const id = parseInt(tokenIdsFlattened[counter]);
        const rarity = parseInt(tokenData.tokenRarity);
        const type = parseInt(tokenData.tokenType);
        const exp = parseInt(tokenData.experiencePoint);
        const price = BigNumber(ordersMetadataFlattened[counter].price._hex).dividedBy(BASE18).toNumber();
        if (collectionId == 1) v1.push({ id, collectionName, collectionId, rarity, type, exp, price });
        else if (collectionId == 2) v2.push({ id, collectionName, collectionId, rarity, type, exp, price });
      }
      counter++;
    }
    v1 = v1.sort((item1, item2) => item2.rarity - item1.rarity || item2.exp - item1.exp || item1.type - item2.type);
    v2 = v2.sort((item1, item2) => item2.rarity - item1.rarity || item2.exp - item1.exp || item1.type - item2.type);
    return { v1, v2 };
  }
);

export const fetchTravaSellingTicketTopProfile = createAsyncThunk(
  "profileSlice/fetchTravaSellingTicketTopProfile",
  async ({ ownerAddress }) => {
    const tokenLength = await NFTTicketMarketplaceContract.getSellOrderCount(ownerAddress);
    const [tokenIds] = await Promise.all([
      multiCall(
        NFTTicketMarketplaceContract.ABI,
        new Array(tokenLength).fill(1).map((_, idx) => ({
          address: NFTTicketMarketplaceContract.ADDRESS,
          name: "getSellOrderAtIndex",
          params: [ownerAddress, idx],
        }))
      ),
    ]);
    const tokenIdsFlattened = tokenIds.flat();

    const [data] = await Promise.all([
      multiCall(
        NFTTicketMarketplaceContract.ABI,
        tokenIdsFlattened.map((tokenId) => ({
          address: NFTTicketMarketplaceContract.ADDRESS,
          name: "getSellOrderInfo",
          params: [tokenId],
        }))
      ),
    ]);
    const dataFlattened = data.flat();

    const [sponsorQueueValid] = await Promise.all([
      multiCall(
        NFTTicketMarketplaceContract.ABI,
        dataFlattened.map((item) => ({
          address: NFTTicketMarketplaceContract.ADDRESS,
          name: "isSponsorQueueValid",
          params: [parseInt(item.sponsorQueue)],
        }))
      ),
    ]);
    const sponsorQueueValidFlattend = sponsorQueueValid.flat();
    const idList = tokenIdsFlattened.filter((_, index) => !sponsorQueueValidFlattend[index]);

    const res = dataFlattened
      .filter((_, index) => !sponsorQueueValidFlattend[index])
      .map((item, idx) => {
        return {
          paymentOption: parseInt(item.paymentOption),
          price: BigNumber(item.price._hex).dividedBy(BASE18).toNumber(),
          seller: item.seller,
          stock: parseInt(item.stock),
          tokenId: parseInt(item.tokenId),
          orderId: parseInt(idList[idx]),
          sponsorQueue: parseInt(item.sponsorQueue),
        };
      })
      .filter((item) => TICKET_IDS.includes(item.tokenId));
    return res;
  }
);

export const fetchTravaSellingTicket = createAsyncThunk(
  "profileSlice/fetchTravaSellingTicket",
  async ({ ownerAddress }) => {
    const tokenLength = await NFTTicketMarketplaceContract.getSellOrderCount(ownerAddress);
    const [tokenIds] = await Promise.all([
      multiCall(
        NFTTicketMarketplaceContract.ABI,
        new Array(tokenLength).fill(1).map((_, idx) => ({
          address: NFTTicketMarketplaceContract.ADDRESS,
          name: "getSellOrderAtIndex",
          params: [ownerAddress, idx],
        }))
      ),
    ]);
    const tokenIdsFlattened = tokenIds.flat();

    const [data] = await Promise.all([
      multiCall(
        NFTTicketMarketplaceContract.ABI,
        tokenIdsFlattened.map((tokenId) => ({
          address: NFTTicketMarketplaceContract.ADDRESS,
          name: "getSellOrderInfo",
          params: [tokenId],
        }))
      ),
    ]);
    const dataFlattened = data.flat();

    const res = dataFlattened
      .map((item, idx) => {
        return {
          paymentOption: parseInt(item.paymentOption),
          price: BigNumber(item.price._hex).dividedBy(BASE18).toNumber(),
          seller: item.seller,
          stock: parseInt(item.stock),
          tokenId: parseInt(item.tokenId),
          orderId: parseInt(tokenIdsFlattened[idx]),
          sponsorQueue: parseInt(item.sponsorQueue),
        };
      })
      .filter((item) => TICKET_IDS.includes(item.tokenId));
    return res;
  }
);

export const fetchVeTravaSelling = createAsyncThunk("profileSlice/fetchVeTravaSelling", async (_, thunkAPI) => {
  const accountAddress = thunkAPI.getState().accountSlice.address;
  const tokenLength = await VeTravaMarketPlaceContract.getTokenOfOwnerBalance(accountAddress);
  const [tokenIds] = await Promise.all([
    multiCall(
      VeTravaMarketPlaceContract.ABI,
      new Array(tokenLength).fill(1).map((_, index) => ({
        address: VeTravaMarketPlaceContract.ADDRESS,
        name: "getTokenOfOwnerAtIndex",
        params: [accountAddress, index],
      }))
    ),
  ]);
  const tokenOnSaleFlattened = tokenIds.flat();
  const [tokensMetadata, tokenVotingPower, tokenOrder] = await Promise.all([
    multiCall(
      VeTravaContract.ABI,
      tokenOnSaleFlattened.map((tokenId) => ({ address: VeTravaContract.ADDRESS, name: "locked", params: [tokenId] }))
    ),
    multiCall(
      VeTravaContract.ABI,
      tokenOnSaleFlattened.map((tokenId) => ({
        address: VeTravaContract.ADDRESS,
        name: "balanceOfNFT",
        params: [tokenId],
      }))
    ),
    multiCall(
      VeTravaMarketPlaceContract.ABI,
      tokenOnSaleFlattened.map((tokenId) => ({
        address: VeTravaMarketPlaceContract.ADDRESS,
        name: "getTokenOrder",
        params: [tokenId],
      }))
    ),
  ]);
  const tokenOrdersFlattened = tokenOrder.flat();
  const { TRAVA_VE_TOKEN_ADDRESS, RTRAVA_VE_TOKEN_ADDRESS } = getCurrentContractAddresses();
  const res = tokenOrdersFlattened.map((item, idx) => {
    const token =
      tokensMetadata[idx].token === TRAVA_VE_TOKEN_ADDRESS
        ? "TRAVA"
        : tokensMetadata[idx].token === RTRAVA_VE_TOKEN_ADDRESS
        ? "rTRAVA"
        : "BNB/TRAVA";
    const id = parseInt(tokenOnSaleFlattened[idx]);
    const votingPower = BigNumber(parseInt(tokenVotingPower[idx])).dividedBy(BASE18).toNumber();
    const rank = getVeTravaRank(votingPower);
    const seller = item.nftSeller || null;
    const price = BigNumber(item.price._hex).dividedBy(BASE18).toNumber();
    return { votingPower, id, rank, seller, price, token };
  });
  return res;
});

export const fetchTravaTicketOwned = createAsyncThunk("profileSlice/fetchTravaTicketOwned", async (_, thunkAPI) => {
  const accountAddress = thunkAPI.getState().accountSlice.address;
  const [balanceType1, balanceType2, balanceType3] = await NFTTicketContract.getAllTicketBalance(
    [accountAddress, accountAddress, accountAddress],
    TICKET_IDS
  );
  return { counter: balanceType1, presale: balanceType2, incentive: balanceType3 };
});

export const updateTravaSellingNFTs = createAsyncThunk(
  "profileSlice/updateTravaSellingNFTs",
  async ({ itemId, version }, thunkAPI) => {
    const soldItems = thunkAPI.getState().profileSlice.selling;
    let index = -1;
    if (version == "1") {
      const _index = soldItems.v1.findIndex((item) => item.id == itemId);
      if (_index >= 0) index = _index;
    } else if (version == "2") {
      const _index = soldItems.v2.findIndex((item) => item.id == itemId);
      if (_index >= 0) index = _index;
    }
    if (index >= 0) {
      const orderMetadata = await NFTSellContract.getTokenOrder(itemId);
      return { index, price: orderMetadata.price, version };
    }
    return { index };
  }
);

export const fetchTravaBuyingOrders = createAsyncThunk(
  "profileSlice/fetchTravaBuyingOrders",
  async ({ ownerAddress }) => {
    const countBuyOrders = await NFTBuyContract.getBuyOrderOfOwnerBalance(ownerAddress);
    const [buyOrderIds] = await Promise.all([
      multiCall(
        NFTBuyContract.ABI,
        new Array(countBuyOrders).fill(1).map((_, idx) => ({
          address: NFTBuyContract.ADDRESS,
          name: "getBuyOrderfOwnerAtIndex",
          params: [ownerAddress, idx],
        }))
      ),
    ]);
    const buyOrderIdsFlattened = buyOrderIds.flat();
    const [setIds, buyOrders] = await Promise.all([
      multiCall(
        NFTCoreContract.ABI,
        buyOrderIdsFlattened.map((tokenId) => ({
          address: NFTCoreContract.ADDRESS,
          name: "getSet",
          params: [tokenId],
        }))
      ),
      multiCall(
        NFTBuyContract.ABI,
        buyOrderIdsFlattened.map((tokenId) => ({
          address: NFTBuyContract.ADDRESS,
          name: "getBuyOrder",
          params: [tokenId],
        }))
      ),
    ]);
    const buyOrdersFlattened = buyOrders.flat();
    const setIdsFlattened = setIds.flat();
    let v1 = [];
    let index = 0;
    for (const buyData of buyOrdersFlattened) {
      const setId = parseInt(setIdsFlattened[index]);
      const id = parseInt(buyOrderIdsFlattened[index]);
      const rarity = parseInt(buyData.tokenRarity);
      const type = parseInt(buyData.tokenType);
      const exp = parseInt(buyData.experienceRequirement);
      const price = BigNumber(buyData.price._hex).dividedBy(BASE18).toNumber();
      const buyerAddress = buyData.nftBuyer;
      v1.push({ id, rarity, type, exp, price, setId, buyerAddress });
    }
    v1 = v1.sort((i1, i2) => i2.rarity - i1.rarity || i2.exp - i1.exp || i1.type - i2.type);
    return { v1, v2: [] };
  }
);

export const createTravaBuyingOrder = createAsyncThunk("profileSlice/createTravaBuyOrder", async ({ orderId }) => {
  const order = await NFTBuyContract.getBuyOrder(orderId);
  if (order) {
    const data = {
      id: parseInt(orderId),
      rarity: parseInt(order.tokenRarity),
      type: parseInt(order.tokenType),
      exp: parseInt(order.experienceRequirement),
      price: BigNumber(order.price).dividedBy(BASE18).toNumber(),
      buyerAddress: order.nftBuyer,
    };
    return { data };
  }
});

export const updateTravaBuyOrder = createAsyncThunk("profileSlice/updateTravaBuyOrder", async ({ orderId }) => {
  const order = await NFTBuyContract.getBuyOrder(orderId);
  if (order) {
    const data = {
      id: parseInt(orderId),
      rarity: parseInt(order.tokenRarity),
      type: parseInt(order.tokenType),
      exp: parseInt(order.experienceRequirement),
      price: BigNumber(order.price).dividedBy(BASE18).toNumber(),
      buyerAddress: order.nftBuyer,
    };
    return { data };
  }
});

export const fetchTravaAuction = createAsyncThunk("profileSlice/fetchTravaAuction", async ({ ownerAddress }) => {
  const countTravaAuction = await NFTAuctionContract.getTokenOfOwnerBalance(ownerAddress);
  const [auctionIds] = await Promise.all([
    multiCall(
      NFTAuctionContract.ABI,
      new Array(countTravaAuction).fill(1).map((_, idx) => ({
        address: NFTAuctionContract.ADDRESS,
        name: "getTokenOfOwnerAtIndex",
        params: [ownerAddress, idx],
      }))
    ),
  ]);
  const collectionIdsFlattened = auctionIds.flat();
  let [collectionSetIds, collectionsMetadata, collectionsExp, collectionOrder] = await Promise.all([
    multiCall(
      NFTCollectionContract.ABI,
      collectionIdsFlattened.map((collectionId) => ({
        address: NFTCollectionContract.ADDRESS,
        name: "getCollectionSetId",
        params: [collectionId],
      }))
    ),
    multiCall(
      NFTCollectionContract.ABI,
      collectionIdsFlattened.map((collectionId) => ({
        address: NFTCollectionContract.ADDRESS,
        name: "getCollectionMetadata",
        params: [collectionId],
      }))
    ),
    multiCall(
      NFTCollectionContract.ABI,
      collectionIdsFlattened.map((collectionId) => ({
        address: NFTCollectionContract.ADDRESS,
        name: "getCollectionExperience",
        params: [collectionId],
      }))
    ),
    multiCall(
      NFTAuctionContract.ABI,
      collectionIdsFlattened.map((tokenId) => ({
        address: NFTAuctionContract.ADDRESS,
        name: "getTokenOrder",
        params: [tokenId],
      }))
    ),
  ]);
  collectionsExp = collectionsExp.flat();
  collectionOrder = collectionOrder.flat();
  collectionsMetadata = collectionsMetadata.map(([components, tokenRarity], idx) => ({
    armorTokenId: parseInt(components.armorTokenId),
    helmetTokenId: parseInt(components.helmetTokenId),
    shieldTokenId: parseInt(components.shieldTokenId),
    weaponTokenId: parseInt(components.swordTokenId),
    rarity: parseInt(tokenRarity),
    setId: parseInt(collectionSetIds[idx]),
    id: parseInt(collectionIdsFlattened[idx]),
    exp: parseInt(collectionsExp[idx]),
    startingBid: BigNumber(parseInt(collectionOrder[idx].startingBid)).dividedBy(BASE18).toNumber(),
    bidSteps: parseInt(collectionOrder[idx].bidSteps),
    endTime: parseInt(collectionOrder[idx].endTime._hex) * 1000,
  }));

  let normalCollections = collectionsMetadata.filter((item) => item.rarity <= 5 && item.rarity >= 1);
  let specialCollections = collectionsMetadata.filter((item) => item.rarity > 5);

  // fetch special collections metadata
  let [tokenURIList] = await Promise.all([
    multiCall(
      NFTCollectionContract.ABI,
      specialCollections.map((item) => ({
        address: NFTCollectionContract.ADDRESS,
        name: "tokenURI",
        params: [item.id],
      }))
    ),
  ]);
  tokenURIList = tokenURIList.flat();
  specialCollections = specialCollections.map((item, idx) => ({
    ...item,
    metadataLink: tokenURIList[idx],
  }));

  // fetch normal collections data
  let armorTokenIdArray = [];
  let helmetTokenIdArray = [];
  let shieldTokenIdArray = [];
  let weaponTokenIdArray = [];
  normalCollections.forEach((item, _) => {
    armorTokenIdArray.push(item.armorTokenId);
    helmetTokenIdArray.push(item.helmetTokenId);
    shieldTokenIdArray.push(item.shieldTokenId);
    weaponTokenIdArray.push(item.weaponTokenId);
  });

  let [armorTokensMetadata, helmetTokensMetadata, shieldTokensMetadata, weaponTokensMetadata] = await Promise.all([
    multiCall(
      NFTCoreContract.ABI,
      armorTokenIdArray.map((tokenId) => ({
        address: NFTCoreContract.ADDRESS,
        name: "getTokenMetadata",
        params: [tokenId],
      }))
    ),
    multiCall(
      NFTCoreContract.ABI,
      helmetTokenIdArray.map((tokenId) => ({
        address: NFTCoreContract.ADDRESS,
        name: "getTokenMetadata",
        params: [tokenId],
      }))
    ),
    multiCall(
      NFTCoreContract.ABI,
      shieldTokenIdArray.map((tokenId) => ({
        address: NFTCoreContract.ADDRESS,
        name: "getTokenMetadata",
        params: [tokenId],
      }))
    ),
    multiCall(
      NFTCoreContract.ABI,
      weaponTokenIdArray.map((tokenId) => ({
        address: NFTCoreContract.ADDRESS,
        name: "getTokenMetadata",
        params: [tokenId],
      }))
    ),
  ]);
  armorTokensMetadata = armorTokensMetadata.flat();
  helmetTokensMetadata = helmetTokensMetadata.flat();
  shieldTokensMetadata = shieldTokensMetadata.flat();
  weaponTokensMetadata = weaponTokensMetadata.flat();

  for (let i = 0; i < normalCollections.length; i++) {
    normalCollections[i] = {
      ...normalCollections[i],
      armor: {
        tokenId: parseInt(armorTokenIdArray[i]),
        rarity: parseInt(armorTokensMetadata[i].tokenRarity),
        exp: parseInt(armorTokensMetadata[i].experiencePoint),
      },
      helmet: {
        tokenId: parseInt(helmetTokenIdArray[i]),
        rarity: parseInt(helmetTokensMetadata[i].tokenRarity),
        exp: parseInt(helmetTokensMetadata[i].experiencePoint),
      },
      shield: {
        tokenId: parseInt(shieldTokenIdArray[i]),
        rarity: parseInt(shieldTokensMetadata[i].tokenRarity),
        exp: parseInt(shieldTokensMetadata[i].experiencePoint),
      },
      weapon: {
        tokenId: parseInt(weaponTokenIdArray[i]),
        rarity: parseInt(weaponTokensMetadata[i].tokenRarity),
        exp: parseInt(weaponTokensMetadata[i].experiencePoint),
      },
    };
  }
  const rawResult = specialCollections.concat(normalCollections);
  const sortedResult = rawResult.sort((item1, item2) => {
    if (item1.rarity < item2.rarity) return 1;
    else return -1;
  });
  const v1 = [];
  const v2 = [];
  for (const rawCollection of sortedResult) {
    if (rawCollection.setId == 1) v1.push(rawCollection);
    else if (rawCollection.setId == 2) v2.push(rawCollection);
  }
  return { v1, v2 };
});

export const updateTravaAuction = createAsyncThunk("profileSlice/updateTravaAuction", async ({ index }, thunkAPI) => {
  const auction = thunkAPI.getState().profileSlice.auction[index];
  const auctionMetadata = await NFTAuctionContract.getTokenOrder(auction.id);

  return {
    ...auction,
    startingBid: BigNumber(parseInt(auctionMetadata.startingBid)).dividedBy(BASE18).toNumber(),
  };
});

export const updateKawaiiLeasingNFT = createAsyncThunk(
  "profileSlice/updateKawaiiLeasingNFT",
  async ({ index }, thunkAPI) => {
    const nft = thunkAPI.getState().profileSlice.leasing[index];
    const tokenId = nft.id,
      ownerAddress = nft.currentOwner;
    const data = await NFTRentContract.getNFTRentListing(tokenId, ownerAddress);
    const available = await NFTRentContract.getAvailableStock(ownerAddress, tokenId);
    const offsetAmount = await TokenFaucetContract.getAllowance(ownerAddress, tokenId);
    const quantity =
      parseInt(data.unallocatedTokens) +
      parseInt(offsetAmount) +
      (await NFTRentContract.getTotalRentingNFT(data.orderHistory, data.allocatedTokens));
    return {
      ...nft,
      isBnB: data.isBnB,
      isAvailableForRent: data.isAvailableForRent,
      minTime: parseInt(parseInt(data.minTime) / RENT_CONTRACT_TIME_BASE),
      maxTime: parseInt(parseInt(data.maxTime) / RENT_CONTRACT_TIME_BASE),
      price: parseFloat(BigNumber(data.price).dividedBy(BASE18).toFixed()),
      available,
      quantity,
      offsetAmount,
    };
  }
);

const initialState = {
  history: { deposit: undefined, rent: undefined, withdraw: undefined },
  owned: undefined,
  collectionOwned: undefined,
  veTravaOwned: undefined,
  leasing: undefined,
  renting: undefined,
  selling: { v1: [], v2: [] },
  buying: { v1: [], v2: [] },
  leaseOrder: undefined,
  auction: { v1: [], v2: [] },
  ticketOwned: undefined,
  ticketSelling: undefined,
  ticketSellingTop: undefined,
  veTravaSelling: undefined,
};

const profileSlice = createSlice({
  name: "profileSlice",
  initialState,
  reducers: {
    resetUserHistory(state, _) {
      state.history = { deposit: undefined, rent: undefined, withdraw: undefined };
    },
    resetProfile(state) {
      Object.assign(state, initialState);
    },
    removeSellingNFTByIndex(state, action) {
      const id = action.payload.itemId;
      const version = action.payload.version;
      let realIndex = -1;
      if (version == "1") realIndex = state.selling.v1.findIndex((item) => item.id == id);
      else if (version == "2") realIndex = state.selling.v2.findIndex((item) => item.id == id);
      if (realIndex >= 0) {
        if (version == "1") state.selling.v1 = state.selling.v1.splice(realIndex, 1);
        else if (version == "2") state.selling.v2 = state.selling.v2.splice(realIndex, 1);
      }
    },
    removeSellingTicketByIndex(state, action) {
      const idx = action.payload.index;
      if (state.ticketSelling?.[idx]) {
        state.ticketSelling = state.ticketSelling.slice(0, idx).concat(state.ticketSelling.slice(idx + 1));
      }
    },
    removeBuyOrderById(state, action) {
      const id = action.payload.orderId;
      const index = state.buying.v1.findIndex((item) => item.id == id);
      if (index >= 0) state.buying.v1.splice(index, 1);
    },
    removeOwnedNFTByIndex(state, action) {
      const idx = action.payload.index;
      if (state.owned?.[idx]) {
        state.owned = state.owned.slice(0, idx).concat(state.owned.slice(idx + 1));
      }
    },
    removeOwnedNFTById(state, action) {
      const id = action.payload.tokenId;
      const index = state.owned.findIndex((i) => i.id == id);
      if (index > -1) state.owned.splice(index, 1);
    },
    removeCollectionOwnedNFTByIndex(state, action) {
      const idx = action.payload.index;
      if (state.collectionOwned?.[idx]) {
        state.collectionOwned = state.collectionOwned.slice(0, idx).concat(state.collectionOwned.slice(idx + 1));
      }
    },
    removeAuctionByIndex(state, action) {
      const idx = action.payload.index;
      if (state.auction?.[idx]) {
        state.auction = state.auction.slice(0, idx).concat(state.auction.slice(idx + 1));
      }
    },
    removeVeTravaOwnedByIndex(state, action) {
      const idx = action.payload.index;
      if (state.veTravaOwned?.[idx]) {
        state.veTravaOwned = state.veTravaOwned.slice(0, idx).concat(state.veTravaOwned.slice(idx + 1));
      }
    },
    removeVeTravaSellingByIndex(state, action) {
      const idx = action.payload.index;
      if (state.veTravaSelling?.[idx]) {
        state.veTravaSelling = state.veTravaSelling.slice(0, idx).concat(state.veTravaSelling.slice(idx + 1));
      }
    },
  },
  extraReducers: (builder) =>
    builder
      // kawaii reducers
      .addCase(fetchKawaiiOwnedNFTs.fulfilled, (state, action) => {
        state.owned = action.payload;
      })
      .addCase(fetchKawaiiOwnedNFTs.rejected, (state, action) => {
        state.owned = undefined;
        console.error(action);
      })
      .addCase(fetchKawaiiLeasingNFTs.fulfilled, (state, action) => {
        state.leasing = action.payload;
      })
      .addCase(fetchKawaiiLeasingNFTs.rejected, (state, action) => {
        state.leasing = undefined;
        console.error(action);
      })
      .addCase(updateKawaiiLeasingNFT.fulfilled, (state, action) => {
        const index = action.meta.arg.index;
        state.leasing[index] = action.payload;
      })
      .addCase(updateKawaiiLeasingNFT.rejected, (_, action) => {
        console.error(action);
      })
      .addCase(fetchKawaiiRentingNFTs.fulfilled, (state, action) => {
        state.renting = action.payload;
      })
      .addCase(fetchKawaiiRentingNFTs.rejected, (state, action) => {
        state.renting = undefined;
        console.error(action);
      })

      // trava reducers
      .addCase(fetchVeTravaOwned.pending, (state) => {
        state.veTravaOwned = undefined;
      })
      .addCase(fetchVeTravaOwned.fulfilled, (state, action) => {
        state.veTravaOwned = action.payload;
      })
      .addCase(fetchVeTravaOwned.rejected, (state, action) => {
        state.veTravaOwned = undefined;
        console.error(action);
      })
      .addCase(fetchTravaAuction.fulfilled, (state, action) => {
        state.auction = action.payload;
      })
      .addCase(fetchTravaAuction.rejected, (state, action) => {
        state.auction = undefined;
        console.error(action);
      })
      .addCase(fetchTravaSellingNFTs.fulfilled, (state, action) => {
        state.selling = action.payload;
      })
      .addCase(fetchTravaSellingNFTs.rejected, (state, action) => {
        state.selling = { v1: [], v2: [] };
        console.error(action);
      })
      .addCase(fetchTravaSellingTicket.fulfilled, (state, action) => {
        state.ticketSelling = action.payload;
      })
      .addCase(fetchTravaSellingTicket.rejected, (state, action) => {
        state.ticketSelling = undefined;
        console.error(action);
      })
      .addCase(fetchVeTravaSelling.fulfilled, (state, action) => {
        state.veTravaSelling = action.payload;
      })
      .addCase(fetchVeTravaSelling.rejected, (state, action) => {
        state.veTravaSelling = undefined;
        console.error(action);
      })
      .addCase(updateTravaSellingNFTs.fulfilled, (state, action) => {
        const index = action.meta.arg.index;
        if (index >= 0) {
          const price = action.meta.arg.price;
          const version = action.meta.arg.version;
          if (version == "1") state.selling.v1[index]["price"] = price;
          else if (version == "2") state.selling.v2[index]["price"] = price;
        }
      })
      .addCase(fetchTravaBuyingOrders.fulfilled, (state, action) => {
        state.buying = action.payload;
      })
      .addCase(fetchTravaBuyingOrders.rejected, (state, action) => {
        state.buying = { v1: [], v2: [] };
        console.error(action);
      })
      .addCase(createTravaBuyingOrder.fulfilled, (state, action) => {
        const data = action.payload.data;
        state.buying.v1.push(data);
      })
      .addCase(updateTravaBuyOrder.fulfilled, (state, action) => {
        const data = action.payload.data;
        const index = state.buying.v1.findIndex((item) => item.id == data.id);
        if (index >= 0) state.buying.v1[index] = data;
      })
      .addCase(updateTravaAuction.fulfilled, (state, action) => {
        const idx = action.meta.arg.index;
        state.auction[idx] = action.payload;
      })
      // history reducers
      .addCase(fetchLeaseOrderData.fulfilled, (state, action) => {
        state.leaseOrder = action.payload;
      })
      .addCase(fetchLeaseOrderData.rejected, (state, action) => {
        state.leaseOrder = undefined;
        console.error(action);
      })
      .addCase(fetchDepositHistory.fulfilled, (state, action) => {
        if (state.history.deposit) {
          state.history.deposit.list = state.history.deposit.list.concat(action.payload.list);
          state.history.deposit.list.sort((i1, i2) => i2.startTime - i1.startTime);
          state.history.deposit.metadata = { ...action.payload.metadata };
        } else {
          state.history.deposit = action.payload;
        }
      })
      .addCase(fetchRentHistory.fulfilled, (state, action) => {
        if (state.history.rent) {
          state.history.rent.list = state.history.rent.list.concat(action.payload.list);
          state.history.rent.list.sort((i1, i2) => i2.startTime - i1.startTime);
          state.history.rent.metadata = action.payload.metadata;
        } else {
          state.history.rent = action.payload;
        }
      })
      .addCase(fetchWithdrawHistory.fulfilled, (state, action) => {
        if (state.history.withdraw) {
          state.history.withdraw.list = state.history.withdraw.list.concat(action.payload.list);
          state.history.withdraw.list.sort((i1, i2) => i2.startTime - i1.startTime);
          state.history.withdraw.metadata = action.payload.metadata;
        } else {
          state.history.withdraw = action.payload;
        }
      })
      .addCase(fetchDepositHistory.rejected, (state, _) => {
        state.history.deposit = { list: [], metadata: { total: 0, page: 0, size: 10 } };
      })
      .addCase(fetchRentHistory.rejected, (state, _) => {
        state.history.rent = { list: [], metadata: { total: 0, page: 0, size: 10 } };
      })
      .addCase(fetchWithdrawHistory.rejected, (state, _) => {
        state.history.withdraw = { list: [], metadata: { total: 0, page: 0, size: 10 } };
      })
      .addCase(resetAccountState, (state, _) => {
        Object.assign(state, initialState);
      })
      .addCase(fetchTravaTicketOwned.pending, (state) => {
        state.ticketOwned = undefined;
      })
      .addCase(fetchTravaTicketOwned.fulfilled, (state, action) => {
        state.ticketOwned = action.payload;
      })
      .addCase(fetchTravaTicketOwned.rejected, (state, action) => {
        state.ticketOwned = undefined;
        console.error(action);
      })
      .addCase(fetchTravaSellingTicketTopProfile.fulfilled, (state, action) => {
        state.ticketSellingTop = action.payload;
      }),
});

export default profileSlice.reducer;
export const {
  resetUserHistory,
  resetProfile,
  removeSellingNFTByIndex,
  removeSellingTicketByIndex,
  removeOwnedNFTByIndex,
  removeOwnedNFTById,
  removeCollectionOwnedNFTByIndex,
  removeBuyOrderById,
  removeAuctionByIndex,
  removeVeTravaOwnedByIndex,
  removeVeTravaSellingByIndex,
} = profileSlice.actions;

export const selectProfileSlice = (state) => state.profileSlice;

export const selectOwnedNFTs = createDraftSafeSelector(
  (state) => selectProfileSlice(state).owned,
  (owned) => {
    if (!owned) return null;
    return owned.map((item, index) => ({ ...item, index }));
  }
);

export const selectVeTravaOwned = createDraftSafeSelector(
  (state) => selectProfileSlice(state).veTravaOwned,
  (veTravaOwned) => {
    if (!veTravaOwned) return null;
    return veTravaOwned.map((item, index) => ({
      ...item,
      index,
    }));
  }
);

export const selectTravaTicketOwned = createDraftSafeSelector(
  (state) => selectProfileSlice(state).ticketOwned,
  (ticketOwned) => {
    if (!ticketOwned) return null;
    return Object.keys(ticketOwned)
      .map((key) => ({
        type: key,
        number: ticketOwned[key],
        id: TICKET_TO_ID[key],
      }))
      .filter((el) => el.number > 0);
  }
);

export const selectKawaiiLeasingNFTs = createDraftSafeSelector(
  (state) => selectProfileSlice(state).leasing,
  (leasingNFTs) => {
    if (!leasingNFTs) return null;
    return leasingNFTs.map((item, index) => ({ ...item, index })).filter((item) => item.quantity > 0);
  }
);

export const selectKawaiiRentingList = createDraftSafeSelector(
  (state) => selectProfileSlice(state).renting,
  (rentingNFTs) => {
    if (!rentingNFTs) return null;
    return rentingNFTs;
  }
);

export const selectTravaTicketSellingList = createDraftSafeSelector(
  (state) => selectProfileSlice(state).ticketSelling,
  (sellingTicket) => {
    if (!sellingTicket) return null;
    return sellingTicket.map((item, index) => ({ ...item, index }));
  }
);

export const selectVeTravaSelling = createDraftSafeSelector(
  (state) => selectProfileSlice(state).veTravaSelling,
  (sellingVeTrava) => {
    if (!sellingVeTrava) return null;
    return sellingVeTrava.map((item, index) => ({ ...item, index }));
  }
);

export const selectDepositHistories = createDraftSafeSelector(
  (state) => selectProfileSlice(state).history.deposit,
  (_, props) => props.page,
  (depositHistory, page) => {
    if (!depositHistory) return {};
    let { size, total } = depositHistory.metadata;
    let data = [...depositHistory.list];
    let dataSlice = data.slice(0, (page + 1) * size);
    return {
      length: dataSlice.length,
      total,
      histories: Object.entries(groupHistoriesByDay(dataSlice)),
    };
  }
);

export const selectWithdrawHistories = createDraftSafeSelector(
  (state) => selectProfileSlice(state).history.withdraw,
  (_, props) => props.page,
  (withdrawHistory, page) => {
    if (!withdrawHistory) return {};
    let { size, total } = withdrawHistory.metadata;
    let data = [...withdrawHistory.list];
    let dataSlice = data.slice(0, (page + 1) * size);
    return {
      length: dataSlice.length,
      total,
      histories: Object.entries(groupHistoriesByDay(dataSlice)),
    };
  }
);

export const selectRentHistories = createDraftSafeSelector(
  (state) => selectProfileSlice(state).history.rent,
  (_, props) => props.page,
  (rentHistory, page) => {
    if (!rentHistory) return {};
    let { size, total } = rentHistory.metadata;
    let data = [...rentHistory.list];
    let dataSlice = data.slice(0, (page + 1) * size);
    return {
      length: dataSlice.length,
      total,
      histories: Object.entries(groupHistoriesByDay(dataSlice)),
    };
  }
);

export const selectDepositHistoryMetadata = createDraftSafeSelector(
  (state) => selectProfileSlice(state).history.deposit,
  (depositHistory) => {
    if (!depositHistory) return null;
    return depositHistory.metadata;
  }
);

export const selectRentHistoryMetadata = createDraftSafeSelector(
  (state) => selectProfileSlice(state).history.rent,
  (rentHistory) => {
    if (!rentHistory) return null;
    return rentHistory.metadata;
  }
);

export const selectWithdrawHistoryMetadata = createDraftSafeSelector(
  (state) => selectProfileSlice(state).history.withdraw,
  (withdrawHistory) => {
    if (!withdrawHistory) return null;
    return withdrawHistory.metadata;
  }
);

export const selectLeaseOrderByTokenId = createDraftSafeSelector(
  (state) => selectProfileSlice(state).leaseOrder,
  (_, props) => props.tokenId,
  (leaseOrder, tokenId) => {
    if (!leaseOrder || !isNumeric(tokenId)) return [];
    return leaseOrder.list.filter((item) => item.tokenId === tokenId);
  }
);

export const selectTicketSellingTopProfile = createDraftSafeSelector(
  (state) => selectProfileSlice(state).ticketSellingTop,
  (ticket) => {
    if (!ticket) return null;
    const temp = ticket || [];
    return temp;
  }
);
